import { Button, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { TextSelect } from "lucide-react";
import { useState } from "react";

import {
  getListAdCopiesQueryKey,
  useDeleteAdCopy,
  useDuplicateAdCopy,
  useListAdCopies,
} from "../../../api/generated/endpoints/generation/generation";
import { useListProducts } from "../../../api/generated/endpoints/teams/teams";
import type { AdCopy } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import classes from "./ad-copies.module.css";
import { CreateCopy } from "./CreateCopy";
import { NO_COPIES } from "./SavedCopies";
import { TabCopyEdit } from "./TabCopyEdit";
import { TabCopyList } from "./TabCopyList";

/** How often the list is read while a copy is still written (G8: the fill follows its job). */
const GENERATING_REFETCH_MS = 3_000;

/** The copy shown: the one chosen, or the one an AI start drafted, by its job. */
type Chosen = { copyId: string } | { jobId: string } | null;

/**
 * The Image page's "Ad copy" tab (live's `text-copy-tab`, captures advanced-234/01 to 09): the
 * brand's ad copies, searched and filtered by product, beside the chosen one in live's editor, or
 * "Create Ad copy". A duplicate is chosen once made; a delete, which live does not confirm, moves
 * to the next copy, else the one before.
 */
export function AdCopyTab({ teamId, brandId }: { teamId: string; brandId: string }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [productId, setProductId] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Chosen>(null);
  const [creating, setCreating] = useState(false);
  const copies = useListAdCopies(
    teamId,
    brandId,
    {
      ...(search === "" ? {} : { search }),
      ...(productId === null ? {} : { product_id: productId }),
    },
    {
      query: {
        refetchInterval: (query) =>
          query.state.data?.items.some((copy) => copy.status === "in_progress") === true
            ? GENERATING_REFETCH_MS
            : false,
      },
    },
  );
  const products = useListProducts(teamId, brandId);
  const productNames = new Map((products.data ?? []).map((product) => [product.id, product.name]));
  const items = copies.data?.items ?? [];
  const shown =
    items.find((copy) =>
      chosen === null
        ? false
        : "copyId" in chosen
          ? copy.id === chosen.copyId
          : copy.job_id === chosen.jobId,
    ) ?? items[0];
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getListAdCopiesQueryKey(teamId, brandId) });
  const duplicate = useDuplicateAdCopy({
    mutation: {
      onSuccess: async (copy) => {
        await refresh();
        setChosen({ copyId: copy.id });
      },
    },
  });
  const remove = useDeleteAdCopy({ mutation: { onSuccess: () => refresh() } });
  const busyId = duplicate.isPending || remove.isPending ? "busy" : null;
  const onDuplicate = (copy: AdCopy) => duplicate.mutate({ teamId, brandId, copyId: copy.id });
  const onDelete = (copy: AdCopy) => {
    const at = items.findIndex((each) => each.id === copy.id);
    const next = items[at + 1] ?? items[at - 1];
    remove.mutate(
      { teamId, brandId, copyId: copy.id },
      { onSuccess: () => setChosen(next === undefined ? null : { copyId: next.id }) },
    );
  };
  return (
    <div className={classes.tab}>
      <TabCopyList
        teamId={teamId}
        brandId={brandId}
        copies={items}
        loading={copies.isPending}
        productNames={productNames}
        search={search}
        onSearch={setSearch}
        productId={productId}
        onProduct={setProductId}
        chosenId={creating ? null : (shown?.id ?? null)}
        onChoose={(copy) => {
          setChosen({ copyId: copy.id });
          setCreating(false);
        }}
        busyId={busyId}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
      />
      <div className={classes.tabPane}>
        <div className={classes.tabPaneTop}>
          {creating ? null : <Button onClick={() => setCreating(true)}>New Ad Copy</Button>}
        </div>
        {copies.isError ? <ErrorAlert error={copies.error} /> : null}
        {duplicate.isError ? <ErrorAlert error={duplicate.error} /> : null}
        {remove.isError ? <ErrorAlert error={remove.error} /> : null}
        {creating ? (
          <CreateCopy
            teamId={teamId}
            brandId={brandId}
            onClose={() => setCreating(false)}
            onStarted={(job) => {
              setChosen({ jobId: job.id });
              setCreating(false);
            }}
            onSaved={(copy) => {
              setChosen({ copyId: copy.id });
              setCreating(false);
            }}
          />
        ) : null}
        {!creating && shown !== undefined ? (
          <TabCopyEdit
            // Live's form starts afresh on another copy, or when its copy is written.
            key={`${shown.id} ${shown.status}`}
            teamId={teamId}
            brandId={brandId}
            copy={shown}
            product={productNames.get(shown.product_id) ?? ""}
            busy={busyId !== null}
            onDuplicate={() => onDuplicate(shown)}
            onDelete={() => onDelete(shown)}
          />
        ) : null}
        {!creating && copies.isSuccess && items.length === 0 ? (
          <div className={classes.state}>
            <TextSelect size={40} strokeWidth={1.5} aria-hidden />
            <Text>{NO_COPIES}</Text>
          </div>
        ) : null}
      </div>
    </div>
  );
}
